import { DatabaseIdTypes, CommentTypes, ConstantDefaultCommentGroupsTypes, DateTimeFormatTypes  } from '../../Enums/ChoiceEnums';
import { IntDatabaseIdAndNameModel } from "../Models/IntDatabaseIdAndNameModel";
import { getJson, postJson } from '../ChoiceApi';
import { NoTzDate } from '../DateUtils';
import { DatabaseId, IDatabaseIdModel } from '../Models/DatabaseIdModel';
import { SimpleUserInfoModel } from '../Models/User/SimpleUserInfoModel';
import { UserGroupModel } from '../Models/User/UserGroupModel';

export const comments = {
    async groupComments(idType: DatabaseIdTypes | string | number, id: number): Promise<IExistingCommentModel[]> {
        return (await getJson(`api/Comments/Group/${idType}/${id}`)) as IExistingCommentModel[];
    },
    // // LEGACY SUPPORT ONLY: Please move to groupComments()
    // async groupCommentsByUglyString(uglyString: string): Promise<IExistingCommentModel[]> {
    //     return (await getJson(`api/Comments/Group/${uglyString}`)) as IExistingCommentModel[];  
    // },

    async favoriteUsersOrGroups(): Promise<IntDatabaseIdAndNameModel[]> {
        return (await getJson(`api/Comments/FavoriteUsersOrGroups`)) as IntDatabaseIdAndNameModel[];
    },
    async getAlerts(commentId: number): Promise<number[]> {
        return (await getJson(`api/Comments/Alerts/${commentId}`)) as number[];
    },

    // Returns new comment_id if adding a new comment
    async save(data: ISaveCommentReq): Promise<ISaveCommentResp | undefined> {
        const resp = (await postJson("api/Comments/Save", data)) as ISaveCommentResp;
        if (!resp)
            return;
        return resp;
    },

    async defaultGroups(): Promise<IDefaultCommentGroupModel[]> {
        return (await getJson("api/Comments/Default/Groups")) as IDefaultCommentGroupModel[];
    },

    async defaultComments(defaultCommentGroupId?: number): Promise<IDefaultCommentModel[]> {
        if (!defaultCommentGroupId)
            return (await getJson("api/Comments/Default/Comments")) as IDefaultCommentModel[];
        return (await getJson(`api/Comments/Default/Comments/${defaultCommentGroupId}`)) as IDefaultCommentModel[];
    },

    async insertDefaultComment(default_comment_group_id: number, description: string, commentText: string, alert_types?: number[], audienceIds?: DatabaseId[], biller_action_id?: number): Promise<number> {
        const commentId: IDatabaseIdModel = await postJson("api/Comments/Default/Comment", {
            comment_group_id: default_comment_group_id,
            comment_hdr: description,
            comment_text: commentText,
            alert_types: alert_types ?? [],
            audienceIds: audienceIds ?? [],
            biller_action_id: biller_action_id,            
            save_cmd: "Insert"
        });
        return <number>commentId.id;
    },

    async updateDefaultComment(comment: IDefaultCommentModel): Promise<number> {
        const commentId: IDatabaseIdModel = await postJson("api/Comments/Default/Comment", {
            comment_id: comment.dflt_comment_id,
            comment_group_id: comment.dflt_comment_group_id,
            comment_hdr: comment.description,
            comment_text: comment.commentText,
            alert_types: comment.alert_types ?? [],
            audienceIds: comment.audienceIds ?? [],
            biller_action_id: comment.biller_action_id,
            save_cmd: "Update"
        });
        return <number>commentId.id;
    },

    async getCommentGroupInfo(commentGroupId: number): Promise<ICommentGroupInfo | undefined> {
        return (await getJson(`api/Comments/Group/Info/${commentGroupId}`)) as ICommentGroupInfo
    },

    async getSecurityUserIdInfo(id?: number): Promise<SimpleUserInfoModel | undefined> {
        return (await getJson(`api/User/Simple/security_users_id/${id}`)) as SimpleUserInfoModel
    },

    async getUserGroupIdInfo(id?: number): Promise<UserGroupModel | undefined> {
        return (await getJson(`api/User/GetUserGroup/${id}`)) as UserGroupModel
    }
}

export interface IExistingCommentModel {
    comment_group_id: number,
    comment_id: number,
    comment_text?: string,
    type?: string,
    alert: boolean,
    enter_date?: NoTzDate,
    alert_effective_date?: NoTzDate
    alert_expiration_date?: NoTzDate
    system_user_name?: string,
    followup_date?: NoTzDate
    biller_action_id?: number,
    userOrGroupList?: IDatabaseIdModel[],
    assignedToId?: string | number,
    assignedToIdType?: DatabaseIdTypes,
    billerActionCompleted?: boolean,
    billerActionCompletedDate?: NoTzDate,
    selectedAssignedTo?: IntDatabaseIdAndNameModel,
    includeOnStatement?: boolean
}

export interface ISaveCommentReq {
    parentId: IDatabaseIdModel,
    comment_text: string,
    comment_type: CommentTypes,
    enter_date: NoTzDate,
    has_alert: boolean,
    alert_effective_date?: NoTzDate,
    alert_expiration_date?: NoTzDate,
    alertTypeIds?: number[],
    userOrGroupList?: IDatabaseIdModel[],     // XML with user info.  Modernize!
    followup_date?: NoTzDate,
    biller_action_id?: number,
    comment_id: number | undefined,
    assignedToId?: string | number,
    assignedToIdType?: DatabaseIdTypes,
    billerActionCompleted?: boolean
    includeOnStatement?: boolean
}

export interface ICommentGroupInfo {
    claim_id?: number,
    patient_entity_id?: number
}

export interface ISaveCommentResp {
    comment_id: number,
    comment_group_id: number,
    user_id: string
}

export interface IDefaultCommentGroupModel {
    dflt_comment_group_id: number,
    description: string,
    sort_order: number,
    constant_default_comment_group_id?: number
}

export interface IDefaultCommentModel {
    dflt_comment_id?: number,
    dflt_comment_group_id: number,
    //groupName: string,      // We could remove if the Pinia for default comments is updated to get it
    description: string,
    commentText: string,
    constant_default_comment_group_id?: ConstantDefaultCommentGroupsTypes,

    // Alert info
    alert_types: number[],     // (For: Patient comments)
    audienceIds?: DatabaseId[], // (For: Patient comments)
    biller_action_id?: number     // (For: Claim comments)
}
